import React from 'react';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import {
  buildPerformerPageSchema,
  extractFaqsFromBlocks,
} from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import useHeroImagePosition from '../../components/SeoDiscovery/useHeroImagePosition';
import SeoHeroImagePreload, {
  heroImageSrcSet,
  HERO_IMAGE_SIZES,
} from '../../components/SeoDiscovery/SeoHeroImagePreload';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

const sectionKeyOf = block =>
  block?.content?.sectionKey || block?.blockKey || block?.content?.key || null;

/** Split hub: About first; history/notable after live events list; skip calendar prose. */
const partitionPerformerHubBlocks = contentBlocks => {
  const sections = (contentBlocks || []).filter(
    b => b.blockType === 'section' || b.blockType === 'hero'
  );
  const about = [];
  const afterEvents = [];
  const venueIntro = [];
  const relatedIntro = [];
  sections.forEach(block => {
    const key = sectionKeyOf(block);
    if (key === 'venues_played' || key === 'venue_directory' || key === 'home_venue') {
      venueIntro.push(block);
    } else if (key === 'related_performers') {
      relatedIntro.push(block);
    } else if (
      key === 'event_discovery' ||
      key === 'upcoming_dates' ||
      key === 'upcoming_summary' ||
      key === 'act_overview'
    ) {
      // Live widget owns upcoming events; drop legacy duplicate prose.
    } else if (key === 'about') {
      about.push(block);
    } else if (key === 'history' || key === 'notable_facts') {
      afterEvents.push(block);
    } else {
      about.push(block);
    }
  });
  return { about, afterEvents, venueIntro, relatedIntro };
};

const absoluteUrl = url => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${rootUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
};

const PerformerHeroCredit = ({ image }) => {
  if (!image?.credit && !image?.attribution) return null;
  const label = image.attribution || `Photo: ${image.credit}`;
  const licenseBit =
    image.license && image.license !== 'unsplash'
      ? ` · ${String(image.license).toUpperCase()}`
      : '';
  return (
    <p className={css.heroCredit}>
      {image.creditUrl ? (
        <a href={image.creditUrl} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        label
      )}
      {image.licenseUrl ? (
        <>
          {' '}
          (
          <a href={image.licenseUrl} target="_blank" rel="noopener noreferrer">
            {image.license || 'license'}
          </a>
          )
        </>
      ) : (
        licenseBit
      )}
    </p>
  );
};

const performerSocialSharing = (performer, title, description) => {
  const imageUrl = absoluteUrl(performer?.image?.url);
  if (!imageUrl) {
    return { title, description };
  }
  const imageAlt = performer.image?.alt || `${performer.performerName || 'Performer'} photo`;
  return {
    title,
    description,
    images1200: [{ name: imageAlt, url: imageUrl, width: 1200, height: 630 }],
    images600: [{ name: imageAlt, url: imageUrl, width: 600, height: 314 }],
  };
};

export const SeoDiscoveryPerformerPageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;
  const { ref: heroImageRef, position: heroImagePosition } = useHeroImagePosition();
  const performer = payload?.performer;
  const venuesPlayed = payload?.venuesPlayed || payload?.venues || [];
  const upcomingEvents = payload?.upcomingEvents || [];
  const relatedPerformers = payload?.relatedPerformers || [];
  const contentBlocks = payload?.contentBlocks || [];
  const internalLinks = payload?.internalLinks || [];
  const breadcrumbs = payload?.breadcrumbs || [];
  const meta = payload?.meta || {};
  const page = payload?.page;
  const noIndex = page?.indexState && page.indexState !== 'index';
  const pageTitle =
    meta.title || meta.metaTitle || `${performer?.performerName || 'Performer'} Tickets | PromoTix`;
  const pageDescription =
    meta.description ||
    meta.metaDescription ||
    `Find tickets for upcoming ${performer?.performerName || 'performer'} events on PromoTix.`;
  const { about, afterEvents, venueIntro, relatedIntro } = partitionPerformerHubBlocks(contentBlocks);
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const canonicalPath =
    page?.canonicalPath ||
    `/event-discovery/performers/${performer?.performerSlug || performer?.slug || ''}/`;
  const schema = performer
    ? buildPerformerPageSchema({
        performer,
        canonicalPath,
        rootUrl: rootUrl(),
        breadcrumbs,
        faqs,
        venues: venuesPlayed,
        upcomingEvents,
        evidence: performer?.schemaEvidence || null,
        meta: {
          ...meta,
          metaTitle: pageTitle,
          metaDescription: pageDescription,
        },
      })
    : null;

  if (fetchError?.status === 404) {
    if (staticContext) staticContext.notfound = true;
    return (
      <Page title="Performer not found | PromoTix">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <h1 className={css.h1}>Performer not found</h1>
            <p>
              <a href="/event-discovery/performers/">Browse all performers</a>
            </p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      socialSharing={performerSocialSharing(performer, pageTitle, pageDescription)}
      schema={schema}
      scrollingDisabled={false}
    >
      {noIndex ? (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      ) : null}
      <SeoHeroImagePreload url={performer?.image?.url} />
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {fetchInProgress && !performer ? <p>Loading…</p> : null}
          {fetchError && fetchError.status !== 404 ? <p>Unable to load performer page.</p> : null}
          {performer ? (
            <>
              {breadcrumbs.length ? <SeoBreadcrumbs items={breadcrumbs} /> : null}
              <header className={css.hero}>
                {performer.image?.url ? (
                  <>
                    <div className={css.heroImageFrame} ref={heroImageRef}>
                      {/* LCP element: preloaded in head, high fetch priority, responsive
                          srcset where the host supports resizing (Unsplash). */}
                      <img
                        className={`${css.heroImage} ${css.heroImageActive}`}
                        src={performer.image.url}
                        srcSet={heroImageSrcSet(performer.image.url) || undefined}
                        sizes={heroImageSrcSet(performer.image.url) ? HERO_IMAGE_SIZES : undefined}
                        alt={performer.image.alt || `${performer.performerName} photo`}
                        loading="eager"
                        fetchpriority="high"
                        width={performer.image.width || undefined}
                        height={performer.image.height || undefined}
                        style={{ objectPosition: `50% ${heroImagePosition}%` }}
                      />
                    </div>
                    <PerformerHeroCredit image={performer.image} />
                  </>
                ) : null}
                <div className={css.heroCopy}>
                  <h1 className={css.h1}>{meta.h1 || `${performer.performerName} Tickets`}</h1>
                </div>
              </header>
              <SeoContentSections blocks={about} excludeHero />
              {internalLinks.filter(l => l.placement === 'hub_nav').length ? (
                <nav className={css.section} aria-label="Performer pages">
                  <ul className={css.indexList}>
                    {internalLinks
                      .filter(l => l.placement === 'hub_nav')
                      .map(l => (
                        <li key={l.linkId || l.targetPath}>
                          <a href={l.targetPath}>{l.anchorText}</a>
                        </li>
                      ))}
                  </ul>
                </nav>
              ) : null}
              <SeoUpcomingEvents
                events={upcomingEvents}
                showImages={false}
                title={`Upcoming ${performer.performerName} Events`}
                emptyMessage={
                  payload?.inventorySystemError
                    ? `Live listings for ${performer.performerName} are temporarily unavailable. Check back soon.`
                    : `There are no live listings for ${performer.performerName} right now. Check back soon or browse events on PromoTix.`
                }
              />
              <SeoContentSections blocks={afterEvents} />
              <SeoContentSections blocks={venueIntro} />
              {venuesPlayed.length ? (
                <section className={css.section}>
                  {!venueIntro.length ? <h2>Venues for {performer.performerName}</h2> : null}
                  <ul className={css.indexList}>
                    {venuesPlayed.map(v => (
                      <li key={v.venueId || v.slug || v.venueName}>
                        {v.path ? <a href={v.path}>{v.venueName}</a> : v.venueName}
                        {v.eventCount ? (
                          <span className={css.eventMeta}> — {v.eventCount} events</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <SeoContentSections blocks={relatedIntro} />
              {relatedPerformers.length ? (
                <section className={css.section}>
                  {!relatedIntro.length ? <h2>Related performers</h2> : null}
                  <ul className={css.indexList}>
                    {relatedPerformers.map(p => (
                      <li key={p.path}>
                        <a href={p.path}>{p.performerName}</a>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <SeoFaqSection blocks={contentBlocks} />
              <p>
                <a href="/event-discovery/performers/">Browse all performers</a>
              </p>
            </>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryPerformerPageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => state.SeoDiscoveryPerformerPage || {};

export default connect(mapStateToProps)(SeoDiscoveryPerformerPageComponent);
